import styles from "./search-result-item.module.css";

import { useState } from "react";

import { Flex, message } from "antd";
import { useNavigate } from "react-router-dom";

import TaskIcon from "@/icons/TaskIcon";
import CubIcon from "@/icons/CubIcon";

import SearchFileModal from "@/components/modals/SearchFileModal";

import routes from "@/routes";
import { getIconByType } from "@/utils";
import { getFile } from "@/api/files";

const iconStyle = { fontSize: 20, color: "#BBBBBB" };

const getObjectDefinitionUuid = (data) =>
  data?.definition_uuid || data?.definition?.uuid;

const getResultPath = ({ type, data, isAdmin }) => {
  if (type === "task") {
    return isAdmin
      ? routes.administration.taskDetail(data.uuid)
      : routes.taskDetail(data.uuid);
  }

  if (type === "object") {
    const definitionUuid = getObjectDefinitionUuid(data);

    if (!definitionUuid) return null;

    return isAdmin
      ? routes.administration.objectDetail(definitionUuid, data.uuid)
      : routes.objectDetail(definitionUuid, data.uuid);
  }

  return null;
};

const renderIcon = (type, data) => {
  if (type === "file") {
    return getIconByType(
      data.type,
      data.extension,
      data.element_type,
      iconStyle.fontSize,
      iconStyle.color,
    );
  }

  if (type === "object") {
    return <CubIcon style={iconStyle} />;
  }

  return <TaskIcon style={iconStyle} />;
};

const renderContent = (type, data) => {
  if (type === "task") {
    return (
      <>
        {data.task_number && <span>{`№${data.task_number}`}</span>}
        <span>{data.name}</span>
      </>
    );
  }

  if (type === "file") {
    return (
      <span>
        {data.name || data.original_name}
        {data.extension ? `.${data.extension}` : ""}
      </span>
    );
  }

  return <span>{data.name}</span>;
};

const SearchResultItem = ({ result, isAdmin, onSelect }) => {
  const navigate = useNavigate();

  const [isFileModalOpen, setIsFileModalOpen] = useState(false);

  const { type, data } = result;

  const path = getResultPath({ type, data, isAdmin });

  const handleCloseFileModal = () => {
    setIsFileModalOpen(false);
  };

  const handleClick = () => {
    if (type === "file") {
      if (isFileModalOpen) return;
      setIsFileModalOpen(true);
      return;
    }

    if (path) {
      navigate(path);
      onSelect?.();
    }
  };

  const handleKeyDown = (event) => {
    if (event.key !== "Enter" && event.key !== " ") return;

    event.preventDefault();
    handleClick();
  };

  const handleFileDownload = () => {
    if (!data?.uuid) return;

    const link = document.createElement("a");
    link.href = `/api/file/download?uuid=${data.uuid}`;
    link.download = data.original_name;
    link.click();
  };

  const handleOpenFolder = async () => {
    if (!data?.uuid) return;

    try {
      const file = await getFile(data.uuid, isAdmin);

      const folderUuid = file?.folder_uuid || "catalog";

      const folderPath = isAdmin
        ? routes.administration.folder(folderUuid)
        : routes.folder(folderUuid);

      handleCloseFileModal();

      const params = new URLSearchParams();
      params.set("file_uuid", data.uuid);

      navigate({
        pathname: folderPath,
        search: `?${params.toString()}`,
      });

      onSelect?.();
    } catch (e) {
      console.error(e);
      message.open({
        type: "error",
        content: e?.message || "Не удалось открыть папку",
      });
    }
  };

  return (
    <>
      <Flex
        gap={10}
        align="center"
        className={styles.searchResultItem}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        role="button"
        tabIndex={0}
      >
        {renderIcon(type, data)}
        {renderContent(type, data)}
      </Flex>

      {type === "file" && isFileModalOpen && (
        <SearchFileModal
          file={data}
          visible={isFileModalOpen}
          isAdmin={isAdmin}
          onCancel={handleCloseFileModal}
          onDownload={handleFileDownload}
          onOpenFolder={handleOpenFolder}
        />
      )}
    </>
  );
};

export default SearchResultItem;
